"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import Paperwork, { type Page } from "./Paperwork";
import {
  answerCapMs,
  answering,
  browserAudioSession,
  captureMicrophone,
  createVoiceController,
  hasAudioSession,
  latencyReadout,
  MAX_ANSWER_S,
  QUIET,
  showsLatency,
  SILENT_MODE_HINT,
  silentModeHint,
  spaceToggles,
  voiceService,
  webAudioPlayer,
  type AudioPlayer,
  type Mode,
  type VoiceController,
  type VoiceState,
} from "./voice";

/**
 * The live oral, over the v6 exam server's WebSocket.
 *
 * It speaks only the four things the protocol has: POST /exams to begin, then
 * `{"say": ...}` out and `{type:"said"|"ready"|"busy"|"ended"|"determination"|"error"}`
 * in. It has no access to the card, the grading or the verdict until the exam ends — the
 * server only ever sends who spoke and what they said, which is deliberate.
 *
 * The exam is spoken: the examiner's lines are read aloud and the student answers out
 * loud between a Start tap and an End tap (or the spacebar), through the voice
 * controller in `./voice`. This page only feeds it socket events,
 * sends what it hands back, and renders its state; every voice decision is made there.
 * Any voice failure lands in chat, which is this page as it always was, and the switch in
 * the header goes between the two at any moment over the same exam.
 */

const SERVER =
  process.env.NEXT_PUBLIC_ORAL_SERVER_URL ?? "http://localhost:8900";
const VOICE_SERVER =
  process.env.NEXT_PUBLIC_VOICE_SERVER_URL ?? "http://localhost:8901";

//: THE STUDENT'S LAST CHOICE OF SWITCH, kept in the browser. Only a convenience: storage
//: can be blocked or full, and then the oral simply opens in voice.
const MODE_KEY = "prepex.oral.mode";

function preferredMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "chat" ? "chat" : "voice";
  } catch {
    return "voice";
  }
}

function rememberMode(mode: Mode) {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {}
}

type Line = { who: "examiner" | "applicant"; said: string };
type Phase =
  | "idle"
  | "starting"
  | "waiting"
  | "yourTurn"
  | "ended"
  | "full"
  | "error";

/** The one line the voice bar shows in place of the answer box. */
function micStatus(voice: VoiceState): string {
  if (voice.opening) return "Opening the microphone…";
  if (voice.recording) return "Recording… Take your time. Tap End answer when you’re done.";
  if (voice.transcribing) return "Got it…";
  if (voice.canStart) return "Your turn. Tap Start answer, then answer out loud.";
  return "Wait for the examiner…";
}

/** m:ss */
function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function OralChat() {
  const [lines, setLines] = useState<Line[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<string | null>(null);
  const [sid, setSid] = useState<string | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [voice, setVoice] = useState<VoiceState>(QUIET);
  const [timed, setTimed] = useState(false);
  const [capMs, setCapMs] = useState(MAX_ANSWER_S * 1000);
  const [silentHint, setSilentHint] = useState(false);
  //: The page's clock while recording, ticked for the timer; the controller keeps `since`.
  const [tick, setTick] = useState(0);
  const ws = useRef<WebSocket | null>(null);
  const player = useRef<AudioPlayer | null>(null);
  const speaker = useRef<VoiceController | null>(null);
  const tail = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    tail.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [lines, phase, verdict]);

  useEffect(
    () => () => {
      ws.current?.close();
      speaker.current?.stop();
    },
    [],
  );

  useEffect(() => {
    if (!voice.recording) return;
    const t = setInterval(() => setTick(performance.now()), 250);
    return () => clearInterval(t);
  }, [voice.recording]);

  //: THE SPACEBAR, on a computer: Start, then End. `spaceToggles` leaves text fields and
  //: buttons alone, and nothing happens unless there is an answer to start or end.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const speech = speaker.current;
      if (!speech) return;
      const t = e.target as HTMLElement | null;
      const key = {
        key: e.key,
        repeat: e.repeat,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        altKey: e.altKey,
        target: t ? { tagName: t.tagName, isContentEditable: t.isContentEditable } : null,
      };
      if (!spaceToggles(key)) return;
      const st = speech.state();
      if (!st.canStart && !answering(st)) return;
      e.preventDefault(); // not a page scroll
      speech.toggleAnswer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  //: THE ONE WAY AN ANSWER LEAVES, typed or spoken, so the exam server cannot tell them
  //: apart and the page's turn moves on the same way for both.
  const deliver = useCallback((words: string) => {
    if (!ws.current) return;
    ws.current.send(JSON.stringify({ say: words }));
    setNotice(null);
    setPhase("waiting");
  }, []);

  const start = useCallback(async () => {
    //: UNLOCKED HERE, BEFORE THE FIRST AWAIT. Only this synchronous stretch still counts
    //: as the Begin click, and a browser plays no audio, and asks for no microphone, that
    //: no gesture started.
    player.current ??= webAudioPlayer();
    speaker.current?.stop();
    const cap = answerCapMs(window.location.search);
    const speech = createVoiceController({
      player: player.current,
      voice: voiceService(VOICE_SERVER),
      //: The microphone opens at each Start tap, never here: no open mic before the
      //: student chooses to answer.
      mic: captureMicrophone(cap / 1000),
      session: browserAudioSession(navigator),
      send: deliver,
      onChange: setVoice,
      maxAnswerMs: cap,
    });
    speech.unlock();
    if (preferredMode() === "chat") speech.switchTo("chat");
    speaker.current = speech;
    setCapMs(cap);
    setSilentHint(
      silentModeHint({
        userAgent: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        hasAudioSession: hasAudioSession(navigator),
      }),
    );
    setVoice(speech.state());
    setTimed(showsLatency(window.location.search));

    setPhase("starting");
    setNotice(null);
    try {
      const r = await fetch(`${SERVER}/exams?model=opus`, { method: "POST" });
      if (r.status === 503) {
        //: At capacity, not broken. The server sends a sentence meant for the reader, so
        //: show it rather than inventing one — and show it as a notice, because being
        //: told the room is full is not the visitor doing anything wrong.
        const { detail } = await r.json().catch(() => ({ detail: null }));
        setPhase("full");
        setNotice(
          detail ??
            "All the examiners are busy right now. Try again in a little while.",
        );
        return;
      }
      if (!r.ok) throw new Error(`the server said ${r.status}`);
      const { id, token } = await r.json();
      setSid(id);

      //: HIS OWN PAPERWORK. The examiner reads the same pages and is told to ask what a
      //: number MEANS for today rather than what it says — so without these on screen he
      //: is being asked to read off a page he does not have.
      fetch(`${SERVER}/exams/${id}/paperwork?token=${token}`)
        .then((res) => (res.ok ? res.json() : { pages: [] }))
        .then((d) => setPages(d.pages ?? []))
        .catch(() => setPages([]));

      const url = `${SERVER.replace(/^http/, "ws")}/exams/${id}/ws?token=${token}`;
      const sock = new WebSocket(url);
      ws.current = sock;
      setPhase("waiting");

      //: A line's log position is its index in this connection's stream of `said`, since
      //: every connection replays the log from the top. The controller speaks by it.
      let position = 0;
      sock.onmessage = (ev) => {
        const m = JSON.parse(ev.data);
        if (m.type === "said") {
          setLines((l) => [...l, { who: m.who, said: m.said }]);
          speech.said(position++, m.who, m.said);
        } else if (m.type === "ready") {
          //: YOUR TURN ON `ready`, NOT ON AN EXAMINER LINE. Lines arrive as they are
          //: written, so the first one can come while the examiner is still going, and
          //: an answer sent then would be refused as busy.
          setPhase("yourTurn");
          speech.ready();
        } else if (m.type === "busy") {
          setNotice("Still with the examiner — one answer at a time.");
        } else if (m.type === "ended") {
          setNotice(null);
          setPhase("ended");
          speech.ended();
        } else if (m.type === "determination") {
          setVerdict(m.text);
        } else if (m.type === "error") {
          setNotice(m.text ?? "Something went wrong.");
        }
      };
      sock.onerror = () => {
        speech.ended();
        setPhase("error");
        setNotice(
          "Couldn’t reach the examiner. If you’re running this locally, the exam server needs to be up on port 8900.",
        );
      };
      sock.onclose = () => {
        speech.ended();
        setPhase((p) => (p === "ended" ? "ended" : p === "error" ? "error" : "ended"));
      };
    } catch (e) {
      setPhase("error");
      setNotice(
        e instanceof Error
          ? `Couldn’t start the exam — ${e.message}.`
          : "Couldn’t start the exam.",
      );
    }
  }, [deliver]);

  const send = useCallback(() => {
    const words = draft.trim();
    if (!words || phase !== "yourTurn" || !ws.current) return;
    deliver(words);
    speaker.current?.answered();
    setDraft("");
  }, [draft, phase, deliver]);

  //: From the click itself: switching to voice unlocks the player again.
  const switchTo = (mode: Mode) => {
    rememberMode(mode);
    speaker.current?.switchTo(mode);
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (phase === "full") {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="display display-md text-ink">Every examiner is busy</h1>
        <p className="mt-5 lede">{notice}</p>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-faint">
          Each oral is a full exam that runs for a quarter of an hour or more, so a seat
          usually opens up fairly soon.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={start}
            className="pill pill-accent"
          >
            Check again
          </button>
          <a
            href="/demo"
            className="pill pill-quiet"
          >
            Read a real exam meanwhile
          </a>
        </div>
      </div>
    );
  }

  if (phase === "idle") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center">
        <h1 className="display display-lg text-ink">Your oral exam</h1>
        <p className="mt-6 lede">
          You&rsquo;re flying two passengers from Gaithersburg to Easton. The examiner has
          your paperwork, your route and the weather in front of it, and will work through
          the trip with you, asking its questions out loud.
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-soft">
          When it&rsquo;s your turn, tap <strong>Start answer</strong>, answer out loud, then
          tap <strong>End answer</strong>. Take as long as you need. Prefer typing? Switch to{" "}
          <strong>Chat</strong> at the top at any time.
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-faint">
          There&rsquo;s no clock and no question limit. If a question is ambiguous, say so
          and ask &mdash; that costs you nothing.
        </p>
        <button onClick={start} className="pill pill-accent mt-10">
          Begin
        </button>
        <p className="mt-6 text-[13px] text-ink-faint">
          First question takes a few seconds while the examiner reads your file.
        </p>
        <p className="mt-2 text-[13px] text-ink-faint" data-testid="privacy">
          Your voice is turned into text and never kept.
        </p>
      </div>
    );
  }

  return (
    <div
      className="max-w-3xl mx-auto px-6 py-10 md:py-14"
      //: THE VOICE STATE, FOR THE VOICE SUITE (e2e/voice). Read by attribute, never by
      //: text, which CSS can transform (see e2e/oral.mjs).
      data-testid="oral"
      data-phase={phase}
      data-mode={voice.mode}
      data-speaking={voice.speaking}
      data-opening={voice.opening}
      data-recording={voice.recording}
      data-can-start={voice.canStart}
      data-warning={voice.warning}
      data-transcribing={voice.transcribing}
      data-thinking={voice.thinking}
    >
      {/* The hamburger lives in the header: out of the way of the answer box, and well
          clear of the bottom-left corner where the dev-tools badge sits. */}
      <div className="flex items-center justify-between gap-4 mb-7">
        <h1 className="text-[17px] font-semibold tracking-[-0.01em] text-ink">
          Private pilot oral
        </h1>
        <div className="flex items-center gap-3">
          {phase !== "ended" && phase !== "error" && (
            <div
              role="group"
              aria-label="Answer by"
              data-testid="mode"
              className="flex rounded-full bg-ground-alt p-0.5 text-[13px]"
            >
              {(["voice", "chat"] as const).map((m) => (
                <button
                  key={m}
                  data-mode={m}
                  onClick={() => switchTo(m)}
                  aria-pressed={voice.mode === m}
                  className={`rounded-full px-3 py-1 ${
                    voice.mode === m ? "bg-ground text-ink lift" : "text-ink-faint"
                  }`}
                >
                  {m === "voice" ? "Voice" : "Chat"}
                </button>
              ))}
            </div>
          )}
          {sid && (
            <span className="hidden sm:inline text-[11px] text-ink-faint font-mono">
              {sid}
            </span>
          )}
          <Paperwork pages={pages} />
        </div>
      </div>

      <div className="space-y-5">
        {lines.map((l, i) => (
          <div
            key={i}
            data-testid="line"
            data-who={l.who}
            className={l.who === "examiner" ? "" : "pl-6 md:pl-14"}
          >
            <div
              className={`text-[11px] font-medium tracking-[0.06em] uppercase mb-2 ${
                l.who === "examiner" ? "text-ink-soft" : "text-ink-faint"
              }`}
            >
              {l.who === "examiner" ? "Examiner" : "You"}
            </div>
            <div
              data-testid="said"
              className={`rounded-[18px] px-5 py-4 text-[16px] leading-[1.5] tracking-[-0.01em] whitespace-pre-wrap ${
                l.who === "examiner"
                  ? "bg-ground text-ink lift"
                  : "bg-ground-alt text-ink-soft"
              }`}
            >
              {l.said}
            </div>
            {/* The answer just given, until the examiner's reply. */}
            {voice.thinking && !voice.heard && i === lines.length - 1 && l.who === "applicant" && (
              <AnswerSent />
            )}
          </div>
        ))}

        {/* What the microphone heard, until the server's own copy arrives as a line. */}
        {voice.heard && (
          <div data-testid="heard" className="pl-6 md:pl-14">
            <div className="text-[11px] font-medium tracking-[0.06em] uppercase mb-2 text-ink-faint">
              You
            </div>
            <div className="rounded-[18px] px-5 py-4 text-[16px] leading-[1.6] tracking-[-0.01em] whitespace-pre-wrap bg-ground-alt text-ink-soft">
              {voice.heard}
            </div>
            <AnswerSent />
          </div>
        )}

        {/* Opening the file until the first line; after that, the thinking cue runs from
            an answer sent to the examiner's next line and nowhere else. */}
        {(phase === "starting" ||
          (phase === "waiting" && (lines.length === 0 || voice.thinking))) && (
          <div className="flex items-center gap-2.5 text-[14px] text-ink-faint pt-1">
            <span className="flex gap-1">
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className="w-1.5 h-1.5 rounded-full bg-ink-faint/50 animate-bounce"
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
            </span>
            {voice.thinking ? "The examiner is thinking…" : "Opening your file…"}
          </div>
        )}
        <div ref={tail} />
      </div>

      {notice && (
        <div className="mt-6 rounded-2xl bg-amber-50 px-4 py-3 text-[14px] text-amber-900 ring-1 ring-amber-200/70">
          {notice}
        </div>
      )}

      {voice.notice && (
        <div
          data-testid="voice-notice"
          className="mt-6 rounded-2xl bg-amber-50 px-4 py-3 text-[14px] text-amber-900 ring-1 ring-amber-200/70"
        >
          {voice.notice}
        </div>
      )}

      {timed && voice.latency && (
        <p
          data-testid="latency"
          data-total-ms={Math.round(voice.latency.total)}
          data-transcription-ms={Math.round(voice.latency.transcription)}
          data-examiner-ms={Math.round(voice.latency.examiner)}
          data-speech-ms={Math.round(voice.latency.speech)}
          className="mt-4 text-[12px] font-mono text-ink-faint"
        >
          {latencyReadout(voice.latency)}
        </p>
      )}

      {verdict && (
        <div className="mt-10 rounded-[18px] bg-ground overflow-hidden lift">
          <div className="px-5 py-3.5 bg-ground-alt text-[11px] font-medium uppercase tracking-[0.06em] text-ink-soft">
            Your result
          </div>
          <pre className="px-5 py-4 text-[14px] leading-[1.55] text-ink whitespace-pre-wrap font-sans">
            {verdict}
          </pre>
        </div>
      )}

      {phase === "ended" && !verdict && (
        <p className="mt-8 text-[15px] text-ink-faint">The oral is over.</p>
      )}

      {phase !== "ended" && phase !== "error" && voice.mode === "voice" && (
        <div className="mt-8 sticky bottom-4">
          <div
            data-testid="voice"
            className="rounded-[20px] bg-ground/85 backdrop-blur-xl px-5 py-4 lift-lg ring-1 ring-black/5 text-[15px] text-ink-soft"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
              <span
                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  voice.recording ? "bg-red-500 animate-pulse" : "bg-ink-faint/40"
                }`}
              />
              <span className="flex-1 min-w-[12rem]">{micStatus(voice)}</span>
              {voice.recording && voice.since !== null && (
                <span
                  data-testid="timer"
                  className="font-mono tabular-nums text-ink"
                >
                  {clock(tick - voice.since)}
                </span>
              )}
              {/* ONE BUTTON for Start and End, so a focused one the spacebar clicks is
                  always the right one. Off until `ready`: an answer sent while a turn
                  runs is refused as busy and lost. */}
              <button
                data-testid="answer-button"
                data-action={answering(voice) ? "end" : "start"}
                onClick={() => speaker.current?.toggleAnswer()}
                disabled={!voice.canStart && !answering(voice)}
                className="pill pill-accent px-7 py-3 text-[16px] w-full sm:w-auto disabled:bg-[#e8e8ed] disabled:text-ink-faint"
              >
                {answering(voice) ? "End answer" : "Start answer"}
              </button>
            </div>
            {voice.warning && voice.since !== null && (
              <p
                data-testid="cap-warning"
                className="mt-3 text-[14px] text-amber-900"
              >
                {Math.max(0, Math.ceil((capMs - (tick - voice.since)) / 1000))} seconds left.
                Your answer sends itself at {clock(capMs)}.
              </p>
            )}
            {/* Older iOS, which mutes the examiner on silent and offers no way round it. */}
            {silentHint && (
              <p data-testid="silent-hint" className="mt-3 text-[13px] text-ink-faint">
                {SILENT_MODE_HINT}
              </p>
            )}
          </div>
        </div>
      )}

      {phase !== "ended" && phase !== "error" && voice.mode === "chat" && (
        <div className="mt-8 sticky bottom-4">
          <div className="rounded-[20px] bg-ground/85 backdrop-blur-xl p-3 lift-lg ring-1 ring-black/5">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              data-testid="answer"
              disabled={phase !== "yourTurn"}
              rows={3}
              placeholder={
                phase === "yourTurn"
                  ? "Answer the examiner… (Enter to send, Shift+Enter for a new line)"
                  : "Wait for the examiner…"
              }
              className="w-full resize-none bg-transparent px-2.5 py-2 text-[16px] leading-[1.5] tracking-[-0.01em] text-ink placeholder:text-ink-faint focus:outline-none disabled:text-ink-faint"
            />
            <div className="flex items-center justify-between pt-1">
              <span className="text-[12px] text-ink-faint px-2.5">
                Not sure what&rsquo;s being asked? Say so &mdash; it won&rsquo;t cost you.
              </span>
              <button
                onClick={send}
                disabled={phase !== "yourTurn" || !draft.trim()}
                className="pill pill-accent disabled:bg-[#e8e8ed] disabled:text-ink-faint px-5 py-2"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === "error" && (
        <button
          onClick={start}
          className="pill pill-quiet mt-6"
        >
          Try again
        </button>
      )}
    </div>
  );
}

/** Under the answer just given, until the examiner's reply. */
function AnswerSent() {
  return (
    <div
      data-testid="answer-sent"
      className="mt-1.5 text-right text-[12px] font-medium text-ink-faint"
    >
      ✓ Answer sent
    </div>
  );
}
